/**
 * ACTIVITY-CORE — helpers puros de presentación del feed (agnósticos de UI).
 * Los textos humanos (i18n) viven en la app; aquí solo estructura:
 * parseo del patch, agrupación por batch y detección de "hecho offline".
 *
 * UN SOLO CAMINO Y UN SOLO FORMATO (plan 06, E5). El feed se sirve por
 * `GET /api/activity`, así que la fila del feed ES el DTO generado desde
 * `db/activity.rs`. Hasta 2026-07-27 convivía con la fila snake_case del espejo
 * `activity_recent` en el SQLite local y un adaptador a mano entre ambas
 * (`activity-feed.tsx`); ese segundo camino ya no existe.
 */
import type { components } from "@space/client";

/** Fila del feed = entrada del log maestro + si la API la admite en `/revert`. */
export type ActivityRow = components["schemas"]["ActivityFeedEntry"];

export type ActivityAction = "created" | "updated" | "deleted" | "restored" | "reverted";

export interface ActivityFieldChange {
  old: unknown;
  new: unknown;
}

export type ActivityPatch = Record<string, ActivityFieldChange>;

/** Type guard de un cambio `{old, new}` (sin aserciones inseguras). */
function isFieldChange(value: unknown): value is { old?: unknown; new?: unknown } {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

/**
 * Normaliza el `patch` jsonb del DTO a `{campo: {old, new}}`. Nunca lanza: el
 * vocabulario de acciones lo extiende el servidor y una entrada rara no puede
 * tumbar el feed.
 */
export function parseActivityPatch(raw: unknown): ActivityPatch {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return {};
  }
  const patch: ActivityPatch = {};
  for (const [field, change] of Object.entries(raw)) {
    if (isFieldChange(change)) {
      patch[field] = { old: change.old, new: change.new };
    }
  }
  return patch;
}

/** Campos cambiados de una entrada, en orden estable. */
export function changedFields(patch: ActivityPatch): string[] {
  return Object.keys(patch).toSorted();
}

/**
 * Umbral a partir del cual una diferencia clientTs↔serverTs se considera
 * "hecho offline y sincronizado después" (y no mera latencia de red).
 */
export const OFFLINE_LAG_THRESHOLD_MS = 60_000;

/**
 * Milisegundos entre el momento en que el cliente hizo el cambio (clientTs)
 * y su llegada al servidor (serverTs). `null` si falta alguno o no parsea.
 */
export function activityLagMs(row: Pick<ActivityRow, "serverTs" | "clientTs">): number | null {
  if (!row.serverTs || !row.clientTs) {
    return null;
  }
  const server = Date.parse(row.serverTs);
  const client = Date.parse(row.clientTs);
  if (Number.isNaN(server) || Number.isNaN(client)) {
    return null;
  }
  return server - client;
}

/** ¿Se hizo offline? (el cliente lo estampó mucho antes de que llegara al servidor) */
export function isOfflineEdit(
  row: Pick<ActivityRow, "serverTs" | "clientTs">,
  thresholdMs: number = OFFLINE_LAG_THRESHOLD_MS,
): boolean {
  const lag = activityLagMs(row);
  return lag !== null && lag > thresholdMs;
}

export interface ActivityBatchGroup<T extends ActivityRow = ActivityRow> {
  /** batchId común (o el id de la fila si no tiene batch). */
  batchId: string;
  entries: T[];
}

/**
 * Agrupa filas CONSECUTIVAS del feed (ya ordenado serverTs DESC) por batchId:
 * "importó 40 tareas" se pinta como un grupo, no 40 tarjetas. No reordena.
 */
export function groupActivityByBatch<T extends ActivityRow>(
  rows: readonly T[],
): ActivityBatchGroup<T>[] {
  const groups: ActivityBatchGroup<T>[] = [];
  for (const row of rows) {
    const key = row.batchId ?? row.id;
    const last = groups.at(-1);
    if (last && last.batchId === key) {
      last.entries.push(row);
    } else {
      groups.push({ batchId: key, entries: [row] });
    }
  }
  return groups;
}
